<template>
  <section class="assets-dashboard-card">
    <header class="assets-dashboard-card__header">
      <div class="assets-dashboard-title">
        <div class="assets-dashboard-title__icon">
          <i class="bi bi-speedometer2" />
        </div>
        <div class="assets-dashboard-title__text">
          {{ $t('components.assetsDashboard.title') }}
        </div>
      </div>
    </header>

    <div class="assets-dashboard-card__body">
      <div
        v-if="loading"
        class="assets-dashboard-loading"
      >
        <div
          class="spinner-border"
          role="status"
          aria-hidden="true"
        />
        <div class="assets-dashboard-loading__text">
          {{ $t('components.loading') }}
        </div>
      </div>

      <div
        v-else
        class="assets-dashboard-grid"
      >
        <div class="summary-tile summary-tile--total">
          <div class="summary-tile__icon">
            <i class="bi bi-hdd-stack-fill" />
          </div>
          <div class="summary-tile__content">
            <div class="summary-tile__value">
              {{ stats.total }}
            </div>
            <div class="summary-tile__label">
              {{ $t('components.assetsDashboard.totalAssets') }}
            </div>
          </div>
        </div>

        <div class="summary-tile summary-tile--active">
          <div class="summary-tile__icon">
            <i class="bi bi-check-circle-fill" />
          </div>
          <div class="summary-tile__content">
            <div class="summary-tile__value">
              {{ stats.active }}
            </div>
            <div class="summary-tile__label">
              {{ $t('components.assetsDashboard.activeAssets') }}
            </div>
            <div class="summary-tile__meta">
              {{ $t('components.assetsDashboard.last30min') }}
            </div>
          </div>
        </div>

        <div class="summary-tile summary-tile--recent">
          <div class="summary-tile__icon">
            <i class="bi bi-clock-history" />
          </div>
          <div class="summary-tile__content">
            <div class="summary-tile__value">
              {{ stats.last24h }}
            </div>
            <div class="summary-tile__label">
              {{ $t('components.assetsDashboard.activeLast24h') }}
            </div>
            <div class="summary-tile__meta">
              {{ $t('components.assetsDashboard.last24hours') }}
            </div>
          </div>
        </div>

        <div class="summary-tile summary-tile--inactive">
          <div class="summary-tile__icon">
            <i class="bi bi-x-circle-fill" />
          </div>
          <div class="summary-tile__content">
            <div class="summary-tile__value">
              {{ stats.inactive }}
            </div>
            <div class="summary-tile__label">
              {{ $t('components.assetsDashboard.inactiveAssets') }}
            </div>
            <div class="summary-tile__meta">
              {{ $t('components.assetsDashboard.moreThan30min') }}
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="error"
        class="assets-dashboard-error"
        role="alert"
      >
        <i
          class="bi bi-exclamation-triangle-fill"
          aria-hidden="true"
        />
        <span>{{ $t('components.assetsDashboard.errorLoading') }}: {{ error }}</span>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { getAssetsWithLatestParams } from '@/crud/asset/getters'

export default defineComponent({
  name: 'AssetsDashboard',
  data() {
    return {
      loading: true,
      error: null as string | null,
      stats: {
        total: 0,
        active: 0,
        inactive: 0,
        last24h: 0
      }
    }
  },
  methods: {
    async fetchStats() {
      try {
        this.loading = true
        this.error = null

        const response = await getAssetsWithLatestParams()
        const assets = Array.isArray(response.data.data) ? response.data.data : []

        const now = Math.floor(Date.now() / 1000) // Current timestamp in seconds
        const thirtyMinutesAgo = now - (30 * 60) // 30 minutes ago
        const twentyFourHoursAgo = now - (24 * 60 * 60) // 24 hours ago

        this.stats.total = assets.length

        // Count active (last_seen < 30 min)
        this.stats.active = assets.filter((asset: any) => {
          return asset.last_seen && asset.last_seen > thirtyMinutesAgo
        }).length

        // Count inactive (last_seen > 30 min or no last_seen)
        this.stats.inactive = assets.filter((asset: any) => {
          return !asset.last_seen || asset.last_seen <= thirtyMinutesAgo
        }).length

        // Count active in last 24h
        this.stats.last24h = assets.filter((asset: any) => {
          return asset.last_seen && asset.last_seen > twentyFourHoursAgo
        }).length

      } catch (err: any) {
        this.error = err.message || 'Unknown error'
        console.error('Error fetching assets stats:', err)
      } finally {
        this.loading = false
      }
    }
  },
  mounted() {
    this.fetchStats()
  }
})
</script>

<style lang="scss" scoped>
.assets-dashboard-card {
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06);
  margin-bottom: 16px;
}

.assets-dashboard-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  background: linear-gradient(180deg, #ffffff 0%, rgba(0, 0, 0, 0.01) 100%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.assets-dashboard-title {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 240px;
}

.assets-dashboard-title__icon {
  width: 40px;
  height: 40px;
  border-radius: 14px;
  background: rgba(var(--primary-color-rgb), 0.14);
  color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  i {
    font-size: 18px;
    color: var(--primary-color);
  }
}

.assets-dashboard-title__text {
  font-size: 18px;
  font-weight: 800;
  color: #252422;
  letter-spacing: -0.01em;
}

.assets-dashboard-card__body {
  padding: 14px 18px 18px;
  background: #ffffff;
}

.assets-dashboard-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px 0;
  color: rgba(108, 117, 125, 0.95);
  font-weight: 700;

  .spinner-border {
    width: 22px;
    height: 22px;
    border-width: 0.2em;
    color: var(--primary-color);
  }
}

.assets-dashboard-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.summary-tile {
  --tile-accent: var(--primary-color);
  --tile-accent-rgb: var(--primary-color-rgb);

  background: rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 16px;
  padding: 14px 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease, background 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 22px rgba(0, 0, 0, 0.08);
    border-color: rgba(0, 0, 0, 0.10);
    background: rgba(var(--tile-accent-rgb), 0.05);
  }
}

.summary-tile__icon {
  width: 40px;
  height: 40px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(var(--tile-accent-rgb), 0.14);
  color: var(--tile-accent);

  i {
    font-size: 20px;
    color: inherit;
  }
}

.summary-tile__content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.summary-tile__value {
  font-size: 22px;
  font-weight: 900;
  color: #252422;
  letter-spacing: -0.01em;
  line-height: 1;
}

.summary-tile__label {
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: rgba(108, 117, 125, 0.95);
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.summary-tile__meta {
  font-size: 12px;
  font-weight: 700;
  color: rgba(108, 117, 125, 0.90);
  line-height: 1;
}

.summary-tile--total {
  --tile-accent: var(--primary-color);
  --tile-accent-rgb: var(--primary-color-rgb);
}

.summary-tile--active {
  --tile-accent: #198754;
  --tile-accent-rgb: 25, 135, 84;
}

.summary-tile--recent {
  --tile-accent: var(--primary-color-soft);
  --tile-accent-rgb: var(--primary-color-soft-rgb);
}

.summary-tile--inactive {
  --tile-accent: #6c757d;
  --tile-accent-rgb: 108, 117, 125;
}

.assets-dashboard-error {
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid rgba(220, 53, 69, 0.25);
  background: rgba(220, 53, 69, 0.08);
  color: #b02a37;
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 700;

  i {
    font-size: 16px;
    color: #dc3545;
    flex-shrink: 0;
  }
}

body.dark-theme {
  .assets-dashboard-card {
    background: #2a2a3c;
    border-color: rgba(255, 255, 255, 0.08);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.35);
  }

  .assets-dashboard-card__header {
    background: #2a2a3c;
    border-bottom-color: rgba(255, 255, 255, 0.08);
  }

  .assets-dashboard-title__text {
    color: #ffffff;
  }

  .assets-dashboard-card__body {
    background: #2a2a3c;
  }

  .assets-dashboard-loading {
    color: rgba(255, 255, 255, 0.70);
  }

  .summary-tile {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.12);

    &:hover {
      box-shadow: 0 10px 22px rgba(0, 0, 0, 0.35);
      border-color: rgba(255, 255, 255, 0.16);
      background: rgba(var(--tile-accent-rgb), 0.10);
    }
  }

  .summary-tile__value {
    color: #ffffff;
  }

  .summary-tile__label,
  .summary-tile__meta {
    color: rgba(255, 255, 255, 0.65);
  }

  .assets-dashboard-error {
    border-color: rgba(220, 53, 69, 0.35);
    background: rgba(220, 53, 69, 0.16);
    color: rgba(255, 255, 255, 0.90);

    i {
      color: #ff6b6b;
    }
  }
}

@media (max-width: 1199px) {
  .assets-dashboard-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .assets-dashboard-card__header {
    padding: 12px;
  }

  .assets-dashboard-card__body {
    padding: 12px;
  }

  .assets-dashboard-grid {
    grid-template-columns: 1fr;
  }

  .summary-tile {
    padding: 12px;
  }
}
</style>
